import React, { useState, useEffect, useRef } from 'react';
import { Badge } from "@vaiu/ui/badge";
import { Button } from "@vaiu/ui/button";
import { ClipboardCopy, MessageCircle, RefreshCw, CheckCircle, X, QrCode, CreditCard, AlertTriangle, Clock, Share2, Smartphone, Zap, Receipt, EyeOff, Eye, Shield, DollarSign } from 'lucide-react';
import { copyToClipboard } from '@vaiu/utils';
import QRCode from 'react-qr-code';
import { Card, CardContent } from '@vaiu/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { roundMonetaryValue, formatMonetaryValue } from '@vaiu/utils/currency';
import {
  formatMmMoneyDisplayTicker,
  isMmMoneyCurrency,
  parseMmMoneyIssuerId,
} from '@vaiu/utils/mmMoney';
import TokenConditioning from '@vaiu/token-ux';
import { useTokenCardDeps } from './TokenCardContext';
import { MmMoneyIssuerDisplay } from './MmMoneyIssuerDisplay';

// Card Brand Logo Components
const VisaLogo = ({ className = "" }: { className?: string }) => (
  <svg 
    viewBox="0 0 48 16" 
    className={`${className}`}
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="#1434CB">
      <path d="M18.5 0.5L15.2 15.5H11.6L14.9 0.5H18.5Z"/>
      <path d="M31.1 0.5C32.3 0.5 33.2 0.8 33.8 1.3C34.4 1.8 34.7 2.6 34.7 3.6C34.7 4.1 34.6 4.5 34.4 4.9C34.2 5.3 33.9 5.6 33.5 5.8C33.1 6 32.6 6.2 32 6.3C31.4 6.4 30.7 6.5 29.9 6.5H28.7L28.1 9.5H29.6C30.2 9.5 30.7 9.4 31.1 9.2C31.5 9 31.8 8.7 32 8.3C32.2 7.9 32.3 7.4 32.3 6.8H35.2C35.2 8.1 34.8 9.2 34 10.1C33.2 11 32.1 11.6 30.7 12C29.3 12.4 27.7 12.6 25.9 12.6H23.4L26.7 0.5H31.1Z"/>
      <path d="M11.1 10.2L7.8 0.5H3.9L0 15.5H3.6L4.2 12.8H8.8L9.4 15.5H13.5L11.1 10.2ZM5.1 9.5L6.1 5.8L7.9 9.5H5.1Z"/>
      <path d="M47.5 10.1C47.5 11.1 47.2 12 46.6 12.7C46 13.4 45.1 13.9 44 14.2C42.9 14.5 41.6 14.7 40.1 14.7C38.6 14.7 37.3 14.5 36.2 14.1C35.1 13.7 34.2 13.1 33.5 12.3C32.8 11.5 32.4 10.5 32.3 9.3H35.8C35.9 9.9 36.1 10.4 36.5 10.8C36.9 11.2 37.4 11.5 38 11.7C38.6 11.9 39.3 12 40.1 12C40.9 12 41.6 11.9 42.2 11.7C42.8 11.5 43.3 11.2 43.6 10.8C43.9 10.4 44.1 9.9 44.1 9.3C44.1 8.7 43.9 8.2 43.5 7.8C43.1 7.4 42.5 7.1 41.7 6.9L39.4 6.3C37.8 5.9 36.6 5.3 35.8 4.5C35 3.7 34.6 2.6 34.6 1.2C34.6 0.2 34.9 -0.7 35.5 -1.4C36.1 -2.1 37 -2.6 38.1 -2.9C39.2 -3.2 40.5 -3.4 42 -3.4C43.5 -3.4 44.8 -3.2 45.9 -2.8C47 -2.4 47.9 -1.8 48.6 -1C49.3 -0.2 49.7 0.8 49.8 2H46.3C46.2 1.4 46 0.9 45.6 0.5C45.2 0.1 44.7 -0.2 44.1 -0.4C43.5 -0.6 42.8 -0.7 42 -0.7C41.2 -0.7 40.5 -0.6 39.9 -0.4C39.3 -0.2 38.8 0.1 38.5 0.5C38.2 0.9 38 1.4 38 2C38 2.6 38.2 3.1 38.6 3.5C39 3.9 39.6 4.2 40.4 4.4L42.7 5C44.3 5.4 45.5 6 46.3 6.8C47.1 7.6 47.5 8.7 47.5 10.1Z"/>
    </g>
  </svg>
);

const MasterCardLogo = ({ className = "" }: { className?: string }) => (
  <svg 
    viewBox="0 0 48 30" 
    className={`${className}`}
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect fill="#FF5F00" x="18" y="6" width="12" height="18"/>
    <circle fill="#EB001B" cx="15" cy="15" r="15"/>
    <circle fill="#F79E1B" cx="33" cy="15" r="15"/>
  </svg>
);

export interface Token {
  key: string;
  amount: number;
  currency: string;
  status: string;
  expiry: string;
  message?: string;
  type?: string; // 'cash' | 'invoice'
  isInvoice?: boolean; // Flag for invoice tokens
  isReferralToken?: boolean; // Flag for referral tokens
  createdAt?: string; // Date when token was created
  // Funding source tracking removed
  prepaidCard?: {
    tokenId: string;
    uniqueUserId: string;
    cardNumber: string;
    cardExpMth: number;
    cardExpYear: number;
    cardCvv: number;
    cardHolderName: string;
    status: string;
    cardCurrency?: string;
    amountLoaded: number;
    currency: string;
    createdAt?: string;
    convertedAt?: string;
    cardBrand?: 'visa' | 'mastercard';
  };
  hasPrepaidCard?: boolean; // Flag to indicate if prepaid card exists
  createdByCurrentUser?: boolean; // Flag to indicate if current user created the token
  createdBy?: string; // User ID of token creator
  invoicePayments?: Array<{
    paymentId: string;
    paidAt: string;
    paidBy: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    transactionId: string;
  }>;
  /** MM$ issuer branding (optional; from token API when available) */
  mmMoneyCurrencyIconUrl?: string;
  mmMoneyBackgroundUrl?: string;
  mmMoneyIssuerDisplayName?: string;
  /** Business / legal name when platform sends it separately from displayName. */
  mmMoneyIssuerName?: string;
  mmMoneyIssuerUsername?: string;
}

export type TokenTheme = 'default' | 'minimal' | 'compact' | 'premium';

function parseInvoiceRemaining(raw: Record<string, unknown>, faceAmount: number): number {
  const data = raw.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : null;
  const invoice = data?.invoice && typeof data.invoice === 'object'
    ? (data.invoice as Record<string, unknown>)
    : null;
  const candidates = [raw.remainingAmount, raw.remainder, invoice?.remainder, data?.remainder];
  for (const candidate of candidates) {
    if (candidate === 0 || candidate === '0') return 0;
    if (candidate == null || candidate === '') continue;
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return faceAmount;
}

interface TokenCardProps {
  token?: Token; // Make optional for backward compatibility
  rawBackendData?: any; // New prop for direct backend data
  theme?: TokenTheme;
  showActions?: boolean;
  showQRCode?: boolean;
  qrMode?: 'direct' | 'button'; // New prop to control QR display mode
  showPrepaidCardOption?: boolean; // New prop to enable prepaid card conversion
  onRefresh?: () => void;
  className?: string;
  borderless?: boolean; // New prop to remove Card wrapper for nested contexts
  /**
   * Opacity (`0–1`) for the MM$ issuer background photo layer behind the frosted tint.
   * Default `1` matches production tokens; previews may use `0.5`.
   */
  mmMoneyBackgroundImageOpacity?: number;
  /** Optional: fetch public merchant profile for MM$ link icon */
  fetchMerchantSummary?: (
    issuerUserId: string
  ) => Promise<import('./MerchantProfileSummary').MerchantPublicSummary | null>;
}

const TokenCard = ({ 
  token, 
  rawBackendData,
  theme = 'default',
  showActions = true,
  showQRCode = true,
  qrMode = 'direct', // Default to direct for backward compatibility
  showPrepaidCardOption = true, // Enable prepaid card option by default
  onRefresh,
  className = '',
  borderless = false,
  mmMoneyBackgroundImageOpacity: mmMoneyBgOpacityProp,
  fetchMerchantSummary: fetchMerchantSummaryProp,
}: TokenCardProps) => {
  const mmMoneyBgOpacity =
    typeof mmMoneyBgOpacityProp === 'number' &&
    Number.isFinite(mmMoneyBgOpacityProp)
      ? Math.min(1, Math.max(0, mmMoneyBgOpacityProp))
      : 1;
  const {
    toast,
    user,
    config,
    boPaymentsApi,
    updateTokenConditions,
    PrepaidCard,
    NFCPayment,
    InvoicePaymentDecision,
  } = useTokenCardDeps();
  // Extract token data directly from raw backend data or use provided token
  const tokenData = rawBackendData ? (() => {
    try {
      // Safety check for rawBackendData
      if (!rawBackendData || typeof rawBackendData !== 'object') {
        console.warn('TokenCard: invalid rawBackendData:', rawBackendData);
        return null;
      }

      // Safe access to nested data property
      const data = rawBackendData.data && typeof rawBackendData.data === 'object' ? rawBackendData.data : null;
      const faceAmount = parseFloat(rawBackendData.amount) || 0;
      const remainingAmount = parseInvoiceRemaining(rawBackendData, faceAmount);

      return {
        key: rawBackendData.key || rawBackendData.code || rawBackendData._id,
        amount: faceAmount,
        remainingAmount,
        isFullyPaid:
          rawBackendData.isFullyPaid === true || remainingAmount <= 0,
        currency: rawBackendData.currency || 'USD',
        status: rawBackendData.status || 'unknown',
        expiry: rawBackendData.validity?.end || 
                (data?.validity?.end) ||
                rawBackendData.expires || 
                rawBackendData.expiry || 
                rawBackendData.expiresAt || 
                rawBackendData.validUntil || 
                rawBackendData.endDate || 
                rawBackendData.valid_until || '',
        message: rawBackendData.message || 
                 (data?.message) || 
                 // Try to extract message from description if it contains " - "
                 (rawBackendData.description && rawBackendData.description.includes(' - ') 
                   ? rawBackendData.description.split(' - ')[1] 
                   : rawBackendData.description) || '',
        type: rawBackendData.tokenType || 'cash',
        isInvoice: rawBackendData.isInvoice || rawBackendData.tokenType === 'invoice' || false,
        isReferralToken: rawBackendData.isReferralToken || false,
        createdAt: rawBackendData.date || rawBackendData.createdAt || rawBackendData.created_at || rawBackendData.dateCreated || '',
        createdBy: rawBackendData.createdBy ?? data?.createdBy,
        createdByCurrentUser: rawBackendData.createdByCurrentUser,
        hasPrepaidCard: rawBackendData.hasPrepaidCard,
        prepaidCard: rawBackendData.prepaidCard,
        mmMoneyCurrencyIconUrl:
          rawBackendData.mmMoneyCurrencyIconUrl ?? data?.mmMoneyCurrencyIconUrl,
        mmMoneyBackgroundUrl: rawBackendData.mmMoneyBackgroundUrl ?? data?.mmMoneyBackgroundUrl,
        mmMoneyIssuerDisplayName:
          rawBackendData.mmMoneyIssuerDisplayName ??
          data?.mmMoneyIssuerDisplayName ??
          rawBackendData.issuerDisplayName,
        mmMoneyIssuerName:
          rawBackendData.mmMoneyIssuerName ??
          data?.mmMoneyIssuerName ??
          rawBackendData.issuerName ??
          rawBackendData.metadata?.merchantName,
        mmMoneyIssuerUsername:
          rawBackendData.mmMoneyIssuerUsername ??
          data?.mmMoneyIssuerUsername ??
          rawBackendData.issuerUsername,
        // Funding source tracking removed
      };
    } catch (error) {
      console.error('❌ Error processing rawBackendData in TokenCard:', error, rawBackendData);
      return null;
    }
  })() : token;

  // All hooks must be called before any conditional returns (React Rules of Hooks)
  const [qrExpanded, setQrExpanded] = useState(false);
  const [showPrepaidCard, setShowPrepaidCard] = useState(false);
  const [showPrepaidCardConfirmation, setShowPrepaidCardConfirmation] = useState(false);
  const [showNFCPayment, setShowNFCPayment] = useState(false);
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  const [prepaidCardData, setPrepaidCardData] = useState<any>(null);
  const [showCard, setShowCard] = useState<boolean>(true);
  const [loadingPrepaidCard, setLoadingPrepaidCard] = useState(false);
  const [selectedCardBrand, setSelectedCardBrand] = useState<'visa' | 'mastercard'>('visa');
  const [showConditioningPanel, setShowConditioningPanel] = useState(false);
  const [invoicePayments, setInvoicePayments] = useState<Array<{
    paymentId: string;
    paidAt: string;
    paidBy: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    transactionId: string;
  }> | null>(null);
  const [loadingInvoicePayments, setLoadingInvoicePayments] = useState(false);
  const [showInvoicePayment, setShowInvoicePayment] = useState(false);
  
  // Request deduplication - prevent multiple simultaneous requests
  const activeRequestRef = useRef<Promise<unknown> | null>(null);
  const fetchedTokensRef = useRef<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);

  // Only log problematic expired tokens (for backend investigation) - but reduce console noise
  if (rawBackendData && tokenData && tokenData.status && tokenData.status.toLowerCase() === 'expired' && !tokenData.expiry) {
    // Only log once per token key to avoid spam
    const logKey = `expired_token_${tokenData.key}`;
    if (!window.hasOwnProperty(logKey)) {
      (window as any)[logKey] = true;
      console.info('ℹ️ EXPIRED TOKEN WITH NO EXPIRY DATE:', {
        key: tokenData.key,
        status: tokenData.status,
        note: 'This is normal for some older tokens. Consider adding expiry date to backend data.',
        possibleExpiryFields: {
          'validity.end': rawBackendData.validity?.end,
          'data.validity.end': rawBackendData.data?.validity?.end,
          'expires': rawBackendData.expires,
          'expiry': rawBackendData.expiry,
          'expiresAt': rawBackendData.expiresAt,
          'validUntil': rawBackendData.validUntil,
          'validity': rawBackendData.validity,
          'data': rawBackendData.data ? Object.keys(rawBackendData.data) : null
        }
      });
    }
  }

  if (!tokenData) {
    console.error('TokenCard: No token data provided');
    return null;
  }

  // Debug logging only - no automatic fetching
  useEffect(() => {
    console.log('🔄 TokenCard mounted/updated:', {
      tokenKey: tokenData.key,
      status: tokenData.status,
      hasCardData: !!prepaidCardData,
      tokenHasCardData: !!tokenData.prepaidCard,
      hasPrepaidCardFlag: tokenData.hasPrepaidCard
    });
    
    // Initialize prepaid card data from token if available and not already set
    if (tokenData.prepaidCard && !prepaidCardData) {
      console.log('💳 Initializing prepaid card data from token:', tokenData.prepaidCard);
      const cardData = {
        ...tokenData.prepaidCard,
        cardNumber: tokenData.prepaidCard.cardNumber.toString(), // Ensure string
        cardCvv: Number(tokenData.prepaidCard.cardCvv), // Ensure number
        cardExpMth: Number(tokenData.prepaidCard.cardExpMth), // Ensure number
        cardExpYear: Number(tokenData.prepaidCard.cardExpYear) // Ensure number
      };
      setPrepaidCardData(cardData);
      fetchedTokensRef.current.add(tokenData.key); // Mark as already have data
    }
  }, [tokenData.key, tokenData.status, tokenData.prepaidCard, prepaidCardData]); // Added tokenData.prepaidCard to dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Use invoice payment details from transaction data
  useEffect(() => {
    if (!tokenData.isInvoice || !rawBackendData || !user) return;

    const rawPayments = rawBackendData.invoicePayments;
    if (!Array.isArray(rawPayments)) return;

    const normalizedPayments = rawPayments.map((payment: any) => ({
      paymentId: payment.paymentId || payment._id || payment.id || '',
      paidAt: payment.paidAt || payment.completedDate || payment.data?.validity?.start || '',
      paidBy: payment.paidBy || payment.userID || '',
      amount: Number(payment.amount ?? payment.data?.amount ?? 0),
      currency: payment.currency || payment.data?.currency || tokenData.currency,
      paymentMethod: payment.paymentMethod || payment.source || 'cash_token',
      transactionId: payment.transactionId || payment._id || payment.id || '',
      tokenKey: payment.tokenKey || payment.key,
    }));
    
    // Check if current user created the invoice
    const currentUserId = user._id || user.id;
    const isInvoiceCreator = rawBackendData.createdByCurrentUser ||
      rawBackendData.createdBy === user.username ||
      rawBackendData.originatorId === currentUserId ||
      rawBackendData.userID === currentUserId;
    
    let paymentsToShow = normalizedPayments;
    
    // If not the invoice creator, only show payments made by the current user
    if (!isInvoiceCreator) {
      paymentsToShow = normalizedPayments.filter((payment: { paidBy?: string }) =>
        payment.paidBy === user.username || payment.paidBy === currentUserId
      );
    }
    
    // Sort payments by date, most recent first
    const sortedPayments = [...paymentsToShow].sort((a, b) => 
      new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime()
    );
    setInvoicePayments(sortedPayments);
  }, [tokenData.isInvoice, rawBackendData, user]);

  const fetchPrepaidCardDetails = async () => {
    // Prevent multiple simultaneous requests for the same token
    if (activeRequestRef.current || loadingPrepaidCard || fetchedTokensRef.current.has(tokenData.key)) {
      console.log('🚫 Skipping request - already in progress or completed for token:', tokenData.key);
      return;
    }

    try {
      setLoadingPrepaidCard(true);
      fetchedTokensRef.current.add(tokenData.key); // Mark as fetched to prevent retries
      
      // Create abort controller for this request
      abortControllerRef.current = new AbortController();
      
      console.log('🔍 Fetching prepaid card details for token:', tokenData.key);
      
      // Store the active request promise
      const requestPromise = boPaymentsApi.getPrepaidCardForToken(tokenData.key);
      activeRequestRef.current = requestPromise;
      
      const response = (await requestPromise) as {
        prepaidCard?: Record<string, unknown>;
      };
      console.log('📋 Full API Response:', response);
      
      if (response && response.prepaidCard) {
        console.log('💳 Setting prepaid card data:', response.prepaidCard);
        const pc = response.prepaidCard;
        // Make sure the card data has the right structure
        const cardData = {
          ...pc,
          cardNumber: String(pc.cardNumber ?? ''),
          cardCvv: Number(pc.cardCvv),
          cardExpMth: Number(pc.cardExpMth),
          cardExpYear: Number(pc.cardExpYear),
        };
        console.log('🔧 Processed card data:', cardData);
        setPrepaidCardData(cardData);
      } else {
        console.log('❌ No prepaid card data found in response:', response);
        // Don't remove from fetchedTokensRef for 404 - it just means no card exists yet
        // This is normal and expected for tokens that haven't been converted to cards
      }
    } catch (error: any) {
      console.error('💥 Error fetching prepaid card details:', error);
      
      // Only remove from fetched set on actual errors (not 404s)
      if (error.response?.status !== 404) {
        fetchedTokensRef.current.delete(tokenData.key);
        
        toast({
          title: "Error loading prepaid card",
          description: "Unable to load prepaid card details. You can try again later.",
          variant: "destructive"
        });
      } else {
        // 404 just means no card exists yet - this is normal, don't show error or retry
        console.log('📝 No prepaid card exists for token:', tokenData.key);
      }
    } finally {
      setLoadingPrepaidCard(false);
      activeRequestRef.current = null; // Clear the active request
      abortControllerRef.current = null;
    }
  };

  const handleCopy = async (e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent event from bubbling to parent clickable containers
    await copyToClipboard(
      tokenData.key,
      (text) => {
        toast({
          title: "Copied to clipboard!",
          description: text,
        });
      },
      (error, text) => {
        toast({
          title: "Copy manually",
          description: `Please copy this code: ${text}`,
          duration: 10000, // Show longer for manual copying
        });
        
        // Try to select the token text for easier manual copying
        const tokenElement = document.querySelector('[data-token-key]');
        if (tokenElement && window.getSelection) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(tokenElement);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
    );
  };

  const handleRefresh = (e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent event from bubbling to parent clickable containers
    if (onRefresh) {
      console.log('🔄 Manual refresh triggered for token:', tokenData?.key);
      onRefresh();
      toast({
        title: "Refreshing token status...",
        description: "Checking for recent activity",
      });
    }
  };

  const handleQRClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't show QR code for expired, completed, or redeemed tokens
    if (tokenData.status.toLowerCase() === 'expired' || 
        tokenData.status.toLowerCase() === 'completed' || 
        tokenData.status.toLowerCase() === 'redeemed') {
      return;
    }
    
    setQrExpanded(!qrExpanded);
  };

  const handlePrepaidCardClick = () => {
    if (isGeneratingCard || showPrepaidCardConfirmation) {
      return; // Prevent multiple clicks
    }
    setShowPrepaidCardConfirmation(true);
  };

  const handleCancelPrepaidCard = () => {
    if (isGeneratingCard) {
      return; // Prevent canceling during card generation
    }
    setShowPrepaidCardConfirmation(false);
  };

  const handleConfirmPrepaidCard = () => {
    if (isGeneratingCard) {
      return; // Prevent multiple confirmations
    }
    setIsGeneratingCard(true);
  };

  const handlePrepaidCardCallback = (result: any) => {
    console.log('Prepaid card created for token:', tokenData.key, result);
    toast({
      title: 'Prepaid Card Issued!',
      description: `Token ${tokenData.key} has been converted to a ${selectedCardBrand.toUpperCase()} prepaid card`,
    });
    setShowPrepaidCardConfirmation(false);
    setIsGeneratingCard(false);
    
    // Immediately set the prepaid card data so it shows up right away
    if (result && result.cardData) {
      console.log('💳 Setting prepaid card data from callback:', result.cardData);
      setPrepaidCardData(result.cardData);
    }
    
    // Clear the fetched cache for this token so it can fetch the new card data
    fetchedTokensRef.current.delete(tokenData.key);
    
    // Don't call onRefresh() here as it causes parent to show "token redeemed" notification
    // The token will naturally update its status through the normal refresh cycle
  };

  // Helper function to get the card brand from prepaid card data
  const getCardBrand = (): 'visa' | 'mastercard' | null => {
    if (prepaidCardData) {
      // Check if prepaid card data has a brand field
      if ((prepaidCardData as any).cardBrand) {
        return (prepaidCardData as any).cardBrand.toLowerCase();
      }
      // Check card number to determine brand (first digit)
      if (prepaidCardData.cardNumber) {
        const firstDigit = prepaidCardData.cardNumber.toString().charAt(0);
        if (firstDigit === '4') return 'visa';
        if (firstDigit === '5') return 'mastercard';
      }
    }
    
    // Check token data for hasPrepaidCard flag
    if (tokenData.hasPrepaidCard === true && tokenData.prepaidCard) {
      if ((tokenData.prepaidCard as any).cardBrand) {
        return (tokenData.prepaidCard as any).cardBrand.toLowerCase();
      }
      // Default to visa if we know card exists but don't know brand
      return 'visa';
    }
    
    return null;
  };

  // Check if token is eligible for prepaid card conversion
  const canConvertToPrepaidCard = (): boolean => {
    if (!tokenData) return false;
    if (isMmMoneyCurrency(tokenData.currency)) return false;

    // Check if token is eligible for prepaid card conversion
    const eligibleStatuses = ['pending', 'active'];
    const isCashToken = tokenData.type === 'cash' || (!tokenData.type && !tokenData.isInvoice);
    const isEligibleStatus = eligibleStatuses.includes(tokenData.status.toLowerCase());
    const isNotInvoice = !tokenData.isInvoice;
    const hasPositiveAmount = tokenData.amount > 0;
    
    // Use configuration-based card issuance rules
    const cardIssuanceRules = config?.ui?.cardIssuanceRules;
    
    let isEligibleForCard = false;
    if (cardIssuanceRules && Object.keys(cardIssuanceRules).length > 0) {
      const currencyRules = cardIssuanceRules[tokenData.currency];
      if (currencyRules) {
        isEligibleForCard = tokenData.amount >= currencyRules.min && tokenData.amount <= currencyRules.max;
      }
    } else {
      // Fallback to legacy logic for backward compatibility
      const isUSDCurrency = tokenData.currency === 'USD';
      const meetsMinimumAmount = tokenData.amount > 0.35;
      isEligibleForCard = isUSDCurrency && meetsMinimumAmount;
    }
    
    return isEligibleStatus && 
           isCashToken && 
           isNotInvoice && 
           hasPositiveAmount &&
           isEligibleForCard &&
           showPrepaidCardOption &&
           showActions;
  };

  // Helper to determine if invoice is revealed (available for payment)
  const isInvoiceRevealed = (): boolean => {
    if (!tokenData.isInvoice) return false;
    const status = tokenData.status.toLowerCase();
    // Invoice is revealed if:
    // 1. Status is explicitly "revealed"
    // 2. Status is "pending" and user didn't create it (it's been revealed to them)
    return status === 'revealed' || 
           (status === 'pending' && !tokenData.createdByCurrentUser && tokenData.createdBy !== user?.id);
  };

  const getStatusBadge = (status: string) => {
    // For invoices, show "Revealed" if it's revealed to the user
    if (tokenData.isInvoice && isInvoiceRevealed()) {
      return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Revealed</Badge>;
    }

    switch (status.toLowerCase()) {
      case 'active':
        // No status badge for active - clean design per user request
        return null;
      case 'pending':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pending</Badge>;
      case 'revealed':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Revealed</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Completed</Badge>;
      case 'redeemed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Redeemed</Badge>;
      case 'expired':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Expired</Badge>;
      case 'card_generated':
        // No status badge for card_generated - we show the visual logo instead
        return null;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <Clock className="w-4 h-4 text-gray-400" />;
      case 'card_generated':
        // No status icon for card_generated - we show the visual logo above QR code instead
        return null;
      default:
        return null;
    }
  };

  const getThemeStyles = () => {
    const isCompleted = tokenData.status.toLowerCase() === 'completed' || tokenData.status.toLowerCase() === 'redeemed';
    const isExpired = tokenData.status.toLowerCase() === 'expired';
    const isCompactState = isCompleted || isExpired;
    
    switch (theme) {
      case 'minimal':
        return {
          container: borderless ? 'p-1 bg-transparent rounded-lg' : (isCompactState ? 'p-2 bg-white shadow-sm rounded-lg' : 'p-3 bg-white shadow-sm rounded-lg'),
          qrSize: 64,
          spacing: 'space-y-1',
          /** Tailwind width on aspect-square MM$ art slot */
          mmArtSquare: 'w-16 shrink-0',
        };
      case 'compact':
        return {
          container: borderless ? 'p-1 bg-transparent rounded-lg' : (isCompactState ? 'p-1.5 bg-white rounded-lg' : 'p-2 bg-white rounded-lg'),
          qrSize: 48,
          spacing: 'space-y-0.5',
          mmArtSquare: 'w-14 shrink-0',
        };
      case 'premium':
        return {
          container: isCompactState ? 'p-3 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-lg rounded-xl' : 'p-6 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-lg rounded-xl',
          qrSize: 128,
          spacing: 'space-y-2',
          mmArtSquare: 'w-28 sm:w-32 shrink-0',
        };
      default:
        return {
          container: borderless ? 'p-1 bg-transparent shadow-none rounded-lg' : (isCompactState ? 'p-2 bg-white shadow-sm rounded-lg' : 'p-4 bg-white shadow-sm rounded-lg'),
          qrSize: 96,
          spacing: 'space-y-1',
          mmArtSquare: 'w-[5.25rem] sm:w-24 shrink-0',
        };
    }
  };

  const themeStyles = getThemeStyles();
  const isMmMoneyToken = isMmMoneyCurrency(tokenData.currency);
  const depositUrl = `https://deposit.${window.location.host}/${tokenData.key}`;
  const isCompletedOrRedeemed = tokenData.status.toLowerCase() === 'completed' || tokenData.status.toLowerCase() === 'redeemed';
  const isExpired = tokenData.status.toLowerCase() === 'expired';

  // Helper function to format time without seconds
  const formatTimeWithoutSeconds = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Helper function to format currency for display (synchronous fallback)
  const formatCurrencySync = (amount: number, currency: string): string => {
    if (isMmMoneyCurrency(currency)) {
      const roundedAmount = roundMonetaryValue(amount, 0);
      const formattedAmount = formatMonetaryValue(roundedAmount, 0);
      return `${formatMmMoneyDisplayTicker()} ${formattedAmount}`;
    }
    // Use currencyService defaults - CHF maps to 'CHF', not '€'
    const currencyDefaults: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'SEK': 'SEK',
      'NOK': 'NOK',
      'DKK': 'DKK',
    };
    const symbol = currencyDefaults[currency.toUpperCase()] || currency;
    // Round first to prevent floating point precision issues (e.g., 1.000003, 8.9999999)
    const roundedAmount = roundMonetaryValue(amount, 2);
    const formattedAmount = formatMonetaryValue(roundedAmount, 2);
    return `${symbol}${formattedAmount}`;
  };

  // Helper function to handle share functionality
  const handleShare = async (e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent event from bubbling to parent clickable containers
    
    // Format currency properly for share text
    const formattedAmount = formatCurrencySync(tokenData.amount, tokenData.currency);
    const shareData = {
      title: `Payment Token ${tokenData.key}`,
      text: `Use this code to redeem ${formattedAmount}: ${tokenData.key}`,
      url: `${window.location.origin}/redeem/${tokenData.key}`
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast({
          title: "Shared successfully!",
          description: "Token shared via device sharing options",
        });
      } else {
        // Fallback to copying share text to clipboard
        const shareText = `${shareData.text}\nRedeem at: ${shareData.url}`;
        await copyToClipboard(shareText);
        toast({
          title: "Share text copied!",
          description: "Token share information copied to clipboard",
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback to copying
      const shareText = `Use code ${tokenData.key} to redeem ${formattedAmount}`;
      await copyToClipboard(shareText);
      toast({
        title: "Share text copied!",
        description: "Token information copied to clipboard",
      });
    }
  };

  // QR Code Enlargement Overlay
  if (qrExpanded && tokenData.status.toLowerCase() !== 'expired' && 
      tokenData.status.toLowerCase() !== 'completed' && 
      tokenData.status.toLowerCase() !== 'redeemed') {
    return (
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4"
        onClick={handleQRClick}
      >
        <div className="relative bg-white rounded-lg p-8 max-w-sm w-full">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-8 w-8 p-0"
            onClick={handleQRClick}
          >
            <X className="w-4 h-4" />
          </Button>
          
          <div className="text-center space-y-4">
            <h3 className="font-bold text-lg">Token: {tokenData.key}</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg inline-block">
              <QRCode value={depositUrl} size={200} title="Deposit QR Code" />
            </div>
            <p className="text-sm text-gray-600">
              Tap the QR code to scan with another device
            </p>
            {showActions && (
              <Button onClick={(e) => handleCopy(e)} className="w-full h-11 text-base touch-manipulation">
                <ClipboardCopy className="w-4 h-4 mr-2" />
                Copy Code
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={`w-full ${className} ${borderless ? 'border-none' : ''}`}>
      <CardContent className={`${themeStyles.container} relative overflow-hidden`}>
        {/* Single column layout — MM$ tokens reserve a fixed square for issuer artwork (image or placeholder). */}
        <div className="relative z-10 flex flex-col gap-1.5">
          <div
            className={isMmMoneyToken ? 'flex gap-3 items-start' : undefined}
          >
            {isMmMoneyToken ? (
              <div
                className={`${themeStyles.mmArtSquare ?? 'w-24 shrink-0'} aspect-square rounded-xl border border-black/[0.08] dark:border-white/[0.12] bg-muted/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] dark:shadow-none relative overflow-hidden ring-1 ring-black/[0.04] ring-inset dark:ring-white/[0.06]`}
                title={
                  tokenData.mmMoneyBackgroundUrl
                    ? 'Issuer card artwork'
                    : 'Issuer artwork (not set)'
                }
              >
                {tokenData.mmMoneyBackgroundUrl ? (
                  <img
                    src={tokenData.mmMoneyBackgroundUrl}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    style={{ opacity: mmMoneyBgOpacity }}
                    draggable={false}
                    decoding="async"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 text-center">
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.14] dark:opacity-[0.09]"
                      style={{
                        backgroundImage:
                          'repeating-linear-gradient(-45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 10px)',
                        color: 'hsl(var(--muted-foreground) / 1)',
                      }}
                    />
                    <span className="relative z-[1] text-[0.625rem] font-black tracking-tighter text-muted-foreground/80 tabular-nums">
                      MM$
                    </span>
                    <span className="relative z-[1] text-[9px] font-medium uppercase tracking-[0.12em] text-muted-foreground/45 leading-none">
                      art
                    </span>
                  </div>
                )}
                {/* Subtle vignette so edges read clearly on busy photos */}
                <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_0_24px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_0_28px_rgba(0,0,0,0.35)]" />
              </div>
            ) : null}
            <div
              className={
                isMmMoneyToken
                  ? `flex-1 min-w-0 ${themeStyles.spacing}`
                  : `flex-1 ${themeStyles.spacing}`
              }
            >
            
            {/* Token code + status */}
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-lg font-bold cursor-pointer select-text truncate min-w-0" data-token-key>
                {tokenData.key}
              </span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {(tokenData.status.toLowerCase() === 'completed' || tokenData.status.toLowerCase() === 'redeemed') && (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                )}
                {getStatusBadge(tokenData.status)}
              </div>
            </div>

            {/* Copy / share / QR and related actions */}
            {showActions && (
              <div className="flex items-center flex-wrap gap-1.5">
                {showQRCode && !isCompletedOrRedeemed && !isExpired && qrMode === 'button' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQRClick(e);
                    }}
                    className="h-10 px-3 gap-1.5 touch-manipulation"
                    title="Show QR Code"
                  >
                    <QrCode className="w-4 h-4" />
                    <span className="text-sm">QR</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleCopy(e)}
                  className="h-10 px-3 gap-1.5 touch-manipulation"
                  title="Copy token code"
                >
                  <ClipboardCopy className="w-4 h-4" />
                  <span className="text-sm">Copy</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleShare(e)}
                  className="h-10 px-3 gap-1.5 touch-manipulation"
                  title="Share token"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Share</span>
                </Button>
                {(tokenData.createdByCurrentUser || tokenData.createdBy === user?.id) &&
                 tokenData.status.toLowerCase() === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConditioningPanel(!showConditioningPanel);
                    }}
                    className="h-10 w-10 p-0 touch-manipulation"
                    title={showConditioningPanel ? 'Hide conditions panel' : 'Add acceptance conditions'}
                  >
                    <Shield className="w-4 h-4" />
                  </Button>
                )}
                {onRefresh && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleRefresh(e)}
                    className="h-10 w-10 p-0 touch-manipulation"
                    title="Refresh token status"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            
            {/* Amount and Expiry on same line */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-lg font-bold">
                {tokenData.isInvoice ? '📧 ' : '💰 '}
                {formatCurrencySync(tokenData.amount, tokenData.currency)}
                {tokenData.isInvoice && <span className="text-xs text-muted-foreground ml-1">(Invoice)</span>}
              </p>
              {tokenData.expiry && (
                <p className="text-xs text-muted-foreground flex-shrink-0">
                  Expires: {formatTimeWithoutSeconds(tokenData.expiry)}
                </p>
              )}
            </div>

            {isMmMoneyCurrency(tokenData.currency) && (
              <div className="pt-0.5">
                <MmMoneyIssuerDisplay
                  currencyKey={tokenData.currency}
                  issuer={{
                    issuerUserId: parseMmMoneyIssuerId(tokenData.currency) || '',
                    currencyIconUrl: tokenData.mmMoneyCurrencyIconUrl,
                    displayName: tokenData.mmMoneyIssuerDisplayName,
                    name: tokenData.mmMoneyIssuerName,
                    username: tokenData.mmMoneyIssuerUsername,
                    backgroundUrl: tokenData.mmMoneyBackgroundUrl,
                  }}
                  fetchMerchantSummary={fetchMerchantSummaryProp}
                  compact
                />
              </div>
            )}
            
            {/* QR Code Display (for direct mode) - Show below if not button mode */}
            {showQRCode && !isCompletedOrRedeemed && !isExpired && qrMode === 'direct' && (
              <div className="flex justify-center py-1">
                <div className="p-1 bg-white rounded border cursor-pointer hover:shadow-sm" onClick={handleQRClick}>
                  <QRCode 
                    value={depositUrl} 
                    size={Math.min(themeStyles.qrSize, 80)}
                    title={`QR Code for ${tokenData.type || 'token'}`}
                  />
                </div>
              </div>
            )}
            
            {/* Issue Card Button - Inline with other info */}
            {canConvertToPrepaidCard() && 
             theme !== 'compact' && 
             !prepaidCardData && 
             !loadingPrepaidCard && 
             !isCompletedOrRedeemed && 
             !isExpired && (
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrepaidCardClick}
                  disabled={showPrepaidCardConfirmation}
                  className="h-10 text-sm px-3 touch-manipulation"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" />
                  {showPrepaidCardConfirmation ? 'Issuing...' : 'Issue Card'}
                </Button>
              </div>
            )}

            {/* Show "Show Card" button if we know a card exists but don't have the data */}
            {canConvertToPrepaidCard() && 
             theme !== 'compact' && 
             !prepaidCardData && 
             tokenData.status.toLowerCase() !== 'expired' &&
             tokenData.hasPrepaidCard === true && (
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPrepaidCardDetails}
                  disabled={loadingPrepaidCard}
                  className="h-10 text-sm px-3 text-purple-600 border-purple-300 hover:bg-purple-50 touch-manipulation"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" />
                  {loadingPrepaidCard ? 'Loading...' : 'Show Card'}
                </Button>
              </div>
            )}

            {/* Pay Invoice Button - Show for revealed invoices that user didn't create */}
            {tokenData.isInvoice && 
             isInvoiceRevealed() &&
             !(tokenData as { isFullyPaid?: boolean }).isFullyPaid && (
              <div className="flex items-center">
                <Button
                  variant="default"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowInvoicePayment(true);
                  }}
                  disabled={showInvoicePayment}
                  className="h-10 text-sm px-4 bg-orange-600 hover:bg-orange-700 text-white touch-manipulation"
                >
                  <DollarSign className="w-4 h-4 mr-1.5" />
                  Pay Invoice
                </Button>
              </div>
            )}

            {/* Invoice Applied Cash Tokens Display */}
            {tokenData.isInvoice && (tokenData as any).paymentCount > 0 && (
              <div className="flex items-center space-x-2 text-xs">
                <div className="flex items-center space-x-1 text-green-600">
                  <Receipt className="w-3 h-3" />
                  <span>{(tokenData as any).paymentCount} payment{(tokenData as any).paymentCount !== 1 ? 's' : ''} applied</span>
                </div>
                {(tokenData as any).isFullyPaid && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                    Fully Paid
                  </span>
                )}
                {(tokenData as any).paymentsByCurrentUser && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                    You Paid
                  </span>
                )}
                {(tokenData as any).createdByCurrentUser && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                    You Created
                  </span>
                )}
              </div>
            )}

            {/* Detailed Invoice Payments */}
            {tokenData.isInvoice && invoicePayments && invoicePayments.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <h4 className="text-sm font-medium mb-2 flex items-center">
                  <Receipt className="w-3 h-3 mr-1" />
                  {(tokenData as any).createdByCurrentUser ? 'Cash Tokens Applied' : 'Your Payment'}
                </h4>
                <div className="space-y-2">
                  {invoicePayments.map((payment, index) => (
                    <div key={payment.paymentId} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        <span className="text-muted-foreground">
                          {(() => {
                            const paidAt = payment.paidAt ? new Date(payment.paidAt) : null;
                            if (!paidAt || Number.isNaN(paidAt.getTime())) {
                              return 'Unknown date';
                            }
                            return `${paidAt.toLocaleDateString()} ${paidAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                          })()}
                        </span>
                        {(tokenData as any).createdByCurrentUser && payment.paidBy && (
                          <span className="text-muted-foreground">by @{payment.paidBy}</span>
                        )}
                      </div>
                      <span className="font-medium text-green-600">
                        {formatCurrencySync(payment.amount, payment.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Status Messages */}
            {tokenData.status === 'redeemed' && (
              <p className="text-xs text-blue-600 font-medium">
                ✅ This token has been successfully redeemed
              </p>
            )}
            
            {/* Cash Token Used for Invoice Payment */}
            {!tokenData.isInvoice && (
              rawBackendData?.usedForInvoice || 
              rawBackendData?.invoiceId || 
              rawBackendData?.invoice_id ||
              rawBackendData?.paidInvoice ||
              rawBackendData?.invoiceKey ||
              rawBackendData?.invoice_key ||
              rawBackendData?.purpose?.includes('invoice') ||
              rawBackendData?.metadata?.type === 'invoice_payment' ||
              rawBackendData?.metadata?.paidInvoice ||
              rawBackendData?.description?.toLowerCase().includes('invoice payment') ||
              rawBackendData?.description?.toLowerCase().includes('paid invoice')
            ) && (
              <p className="text-xs text-orange-600 font-medium">
                📧 This token was used to pay an invoice
                {(rawBackendData?.paidInvoice || rawBackendData?.invoiceKey || rawBackendData?.invoice_key) && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({rawBackendData?.paidInvoice || rawBackendData?.invoiceKey || rawBackendData?.invoice_key})
                  </span>
                )}
              </p>
            )}
            
            {/* Show prepaid card status messages */}
            {tokenData.hasPrepaidCard === true && !prepaidCardData && !loadingPrepaidCard && (
              <p className="text-xs text-purple-600 font-medium">
                💳 This token has a prepaid card available
              </p>
            )}



            {/* Token Message - Compact inline display */}
            {tokenData.message && (
              <div className="flex items-center space-x-1.5 text-sm">
                <MessageCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <p className="text-gray-600 italic text-xs">"{tokenData.message}"</p>
              </div>
            )}

            {/* Funding Source Information - Removed as per backend changes */}
            {/* {tokenData.fundingSource && tokenData.fundingSource.source !== 'balance' && (
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-sm">
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <p className="text-blue-700">
                    Funded with {tokenData.fundingSource.brand || tokenData.fundingSource.source}
                    {tokenData.fundingSource.last4 && ` •••• ${tokenData.fundingSource.last4}`}
                    {tokenData.fundingSource.amount && 
                      ` (${tokenData.fundingSource.currency} ${tokenData.fundingSource.amount.toFixed(2)})`
                    }
                  </p>
                </div>
              </div>
            )} */}

            {/* Legacy: Show 'Check for Card' button for tokens without backend flag (old API responses) */}
            {canConvertToPrepaidCard() && 
             !prepaidCardData && 
             !loadingPrepaidCard && 
             tokenData.hasPrepaidCard === undefined &&
             !fetchedTokensRef.current.has(tokenData.key) && 
             (theme === 'compact' || !showActions) && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPrepaidCardDetails}
                disabled={loadingPrepaidCard}
                className="text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                <CreditCard className="w-3 h-3 mr-1" />
                Check for Prepaid Card
              </Button>
            )}


            </div>
          </div>
        </div>

        {/* Display Prepaid Card Section - header always visible; body toggled */}
        {prepaidCardData && tokenData.status.toLowerCase() !== 'expired' && (
          <div className="mt-1 p-1 bg-purple-50 rounded-lg">
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-purple-900 flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Your Prepaid Card
                </h4>
                {/* Hide card action in header (replaces former NFC Pay) */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 text-purple-700 border-purple-300 hover:bg-purple-50 touch-manipulation"
                  onClick={() => setShowCard(!showCard)}
                  title={showCard ? 'Hide Card' : 'Show Card'}
                >
                  {showCard ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* NFC Payment Component */}
              {showNFCPayment && (
                <div className="bg-white rounded-lg p-1 border border-purple-200">
                  <NFCPayment
                    tokenId={prepaidCardData?.tokenId || (tokenData as any)?.prepaidCard?.tokenId}
                    cardNumber={prepaidCardData.cardNumber}
                    expiryDate={`${prepaidCardData.cardExpMth.toString().padStart(2, '0')}/${prepaidCardData.cardExpYear.toString().slice(-2)}`}
                    cardholderName={prepaidCardData.cardHolderName}
                    onPaymentSent={() => {
                      toast({
                        title: "NFC Payment Ready",
                        description: "Hold your phone near the payment terminal to complete the transaction.",
                        variant: "default"
                      });
                    }}
                  />
                </div>
              )}
              
              {showCard && (
                <PrepaidCard
                  money={{
                    amount: prepaidCardData.amountLoaded || tokenData.amount,
                    currency: prepaidCardData.currency || tokenData.currency
                  }}
                  display="card"
                  status={prepaidCardData.status}
                  className="bg-white"
                  existingCardData={prepaidCardData}
                  tokenCreatedAt={tokenData.createdAt}
                  tokenExpiry={tokenData.expiry}
                  showNfcButton={true}
                  onNfcPayClick={() => setShowNFCPayment(!showNFCPayment)}
                />
              )}
            </div>
          </div>
        )}

        {/* Show loading state if fetching prepaid card data */}
        {loadingPrepaidCard && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-600" />
              <p className="text-sm text-gray-600">Loading prepaid card details...</p>
            </div>
          </div>
        )}

        {/* Inline Prepaid Card Confirmation with Card Brand Selection */}
        {showPrepaidCardConfirmation && tokenData.status.toLowerCase() !== 'expired' && (
          <div className="mt-4 p-6 bg-blue-50 rounded-lg">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-blue-900 text-lg">Issue Prepaid Card</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleCancelPrepaidCard}
                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-sm text-blue-800 leading-relaxed">
                Convert your {tokenData.currency} {formatMonetaryValue(roundMonetaryValue(tokenData.amount, 2), 2)} token into a virtual prepaid card that you can use for online purchases.
              </p>

              {/* Card Brand Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-blue-900">Card Brand:</label>
                <Select value={selectedCardBrand} onValueChange={(value: 'visa' | 'mastercard') => setSelectedCardBrand(value)}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="Select card brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visa">
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600 font-bold">VISA</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="mastercard">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600 font-bold">MASTERCARD</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {isGeneratingCard ? (
                <div className="pt-2">
                  <PrepaidCard
                    money={{
                      amount: tokenData.amount,
                      currency: tokenData.currency
                    }}
                    tokenKey={tokenData.key}
                    autoStart={true}
                    cardCreatedCallback={handlePrepaidCardCallback}
                    cardBrand={selectedCardBrand}
                    tokenCreatedAt={tokenData.createdAt}
                    tokenExpiry={tokenData.expiry}
                  />
                </div>
              ) : (
                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={handleConfirmPrepaidCard} 
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Issue {selectedCardBrand.toUpperCase()} Card
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCancelPrepaidCard}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Inline Token Conditioning Panel */}
        {showConditioningPanel && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-lg flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  Add Token Conditions
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowConditioningPanel(false)}
                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <TokenConditioning
                tokenKey={tokenData.key}
                tokenAmount={tokenData.amount}
                tokenCurrency={tokenData.currency}
                tokenType={(tokenData.type as 'cash' | 'invoice') || 'cash'}
                inline={true}
                toast={toast}
                updateTokenConditions={updateTokenConditions}
                onConditionsSaved={() => {
                  if (onRefresh) onRefresh();
                }}
              />
            </div>
          </div>
        )}

        {/* Invoice Payment Decision Panel */}
        {showInvoicePayment && tokenData.isInvoice && (
          <div
            className="mt-4 pt-4 border-t"
            onClick={(e) => e.stopPropagation()}
          >
            <InvoicePaymentDecision
              invoiceData={{
                invoiceKey: tokenData.key,
                invoiceAmount: tokenData.amount,
                invoiceCurrency: tokenData.currency,
                invoiceMessage: tokenData.message,
                invoiceCreator: tokenData.createdBy,
                remainingAmount:
                  (tokenData as { remainingAmount?: number }).remainingAmount ??
                  ((tokenData as { isFullyPaid?: boolean }).isFullyPaid ? 0 : tokenData.amount),
                autoPayPreferred: false,
              }}
              isGuest={false}
              onPaymentComplete={() => {
                setShowInvoicePayment(false);
                if (onRefresh) {
                  onRefresh();
                }
              }}
              onPaymentError={(error: { message?: string }) => {
                console.error('Invoice payment error:', error);
                toast({
                  title: "Payment Failed",
                  description: error?.message || "Failed to pay invoice. Please try again.",
                  variant: "destructive",
                });
              }}
              onCancel={() => {
                setShowInvoicePayment(false);
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TokenCard;
